import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getThreads, type Thread } from '../../../services/MessageService';
import { useNotification } from '../../../components/Notifications';
import { useAuth } from '../../../security/AuthContext';
import { ThreadRow } from './ThreadRow';
import { Show } from '../../../components/Show';
import { CircleButton } from '../../../components/CircleButton';
import { CircleLink } from '../../../components/CircleLink';

export function ThreadsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const [ searchQuery, setSearchQuery ] = useState('');
  const [ threads, setThreads ] = useState([]);
  const [ showArchived, setShowArchived ] = useState(false);

  useEffect(() => {
    loadThreads();
  }, []);

  useEffect(() => {
    loadThreads();
  }, [showArchived]);

  const loadThreads = () => {
    getThreads({ archived: showArchived })
      .then((data: Thread[]) => {
        setThreads(data);
      })
      .catch((err) => {
        addNotification('Error loading threads ' + err.message, 'error');
      });
  }

  const handleThreadClick = (id) => {
    navigate(`/messages/${id}`);
  };

  const handleRefresh = () => {
    loadThreads();
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredEmails = threads.filter((email) =>
    `${email.recipients.map((r) => r.email).join(' ')} ${email.subject}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Styled Header Block */}
      <div className="bg-white shadow-md rounded-lg overflow-visible mb-6">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-gray-700">
              {showArchived ? 'Archived ' : ''}Threads
            </h1>
            <Show show={!showArchived}>
              <CircleLink
                path="/messages/create"
                icon='fas fa-plus'
                color='text-indigo-500 hover:bg-indigo-600 hover:text-white'
                tooltip='New Thread'
              />
            </Show>
            <CircleButton
              clickHandler={handleRefresh}
              icon='fa-rotate-right fa-solid'
              color='text-indigo-500 hover:bg-indigo-600 hover:text-white'
              tooltip='Refresh Threads'
            />
          </div>

          <div className="flex items-center gap-4">
            <Show show={!showArchived}>
              <CircleLink 
                path='/DeletedThreads'
                icon='fas fa-trash'
                color='text-indigo-500 hover:bg-indigo-600 hover:text-white'
                tooltip='Show Deleted'
              />
            </Show>

            <CircleButton 
              clickHandler={() => setShowArchived(!showArchived)}
              icon={showArchived ? "fa-solid fa-file text-lg" : "fa-solid fa-archive text-lg"}
              color='text-indigo-500 hover:bg-indigo-600 hover:text-white'
              tooltip={showArchived ? 'Show Threads' : 'Show Archived'}
            />

            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search Threads..."
                className="pr-10 pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-gray-800 placeholder-gray-400"
              />
              <i
                className="fa-solid fa-magnifying-glass text-indigo-500 absolute right-3 top-1/2 transform -translate-y-1/2 text-lg"
              ></i>
            </div>
          </div>
        </div>

        {/* Email List */}
        <div role="list" className="divide-y divide-gray-200">
          {filteredEmails.map((thread) => (
            <ThreadRow
              key={thread.id}
              thread={thread}
              handleClick={() => handleThreadClick(thread.id)}
              setThreads={setThreads}
              showArchived={showArchived}
              reloadThreads={loadThreads}
            />
          ))}
        </div>
      </div>
    </div >
  );
}